import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { MagnifyingGlass, Plus, FunnelSimple, Trash, CaretUp, CaretDown, CaretUpDown } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useStatuses } from "../context/StatusContext";
import { StatusBadge, PriorityBadge, ClientRepliedBadge } from "../components/Badges";
import { ALL_STATUSES, PRIORITIES, STATUS_LABELS, canCreateTickets, formatDate, humanizeDuration, initials, slaState, subtypesText } from "../lib/utils";
import { Button } from "../components/ui/button";
import { SearchableSelect } from "../components/ui/searchable-select";
import useFieldConfigs from "../hooks/useFieldConfigs";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";

const DONE_BUCKETS = new Set(["completed", "rejected"]);
const ACTIVE_BUCKETS = new Set(["open", "in_progress", "pending_client", "none"]);

// Inclusive created-at window [from, to] for the selected date filter.
function dateWindow(range, from, to) {
  const now = new Date();
  if (range === "7d") return [new Date(now.getTime() - 7 * 864e5), null];
  if (range === "30d") return [new Date(now.getTime() - 30 * 864e5), null];
  if (range === "month") return [new Date(now.getFullYear(), now.getMonth(), 1), null];
  if (range === "custom") return [from ? new Date(from) : null, to ? new Date(`${to}T23:59:59`) : null];
  return [null, null]; // all time
}

const BUCKET_TABS = { open: "active", in_progress: "active", pending_client: "active", completed: "completed" };

export default function Tickets() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { list: statusList, byKey } = useStatuses();
  const [searchParams] = useSearchParams();
  const initialParams = useRef(searchParams);
  const lastTabParamRef = useRef(searchParams.get("tab"));
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const initialBucket = initialParams.current.get("bucket");
  const initialTabParam = initialParams.current.get("tab");
  const initialTab = initialTabParam === "urgent" ? "urgent" : (BUCKET_TABS[initialBucket] || "active");
  const [loaded, setLoaded] = useState({ active: true, completed: initialTab === "completed", all: false, urgent: initialTab === "urgent" });
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(initialTab);
  const [status, setStatus] = useState("all");
  const [priority, setPriority] = useState("all");
  const [projectId, setProjectId] = useState(initialParams.current.get("project_id") || "all");
  const [organizationId, setOrganizationId] = useState(initialParams.current.get("organization_id") || "all");
  const [bucket, setBucket] = useState(initialBucket || "all");
  const [dateRange, setDateRange] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [search, setSearch] = useState("");
  const [assignedToMe, setAssignedToMe] = useState(false);
  const [clientReplied, setClientReplied] = useState(false);
  const [sortBy, setSortBy] = useState(null); // 'project' | 'status' | 'priority' | 'assignee' | 'age'
  const [sortDir, setSortDir] = useState("asc");
  const isAdmin = user.role === "super_admin";
  const isStaff = ["super_admin", "project_manager", "team_member"].includes(user.role);
  const { fieldOn } = useFieldConfigs();

  // Sync tab from live searchParams for same-route navigations (e.g. the
  // sidebar "Urgent Tickets" link clicked while already on /tickets, which
  // React Router does not remount). Only reacts when the URL's `tab` param
  // actually changes to "urgent" — compared against the last-seen URL value,
  // not the current `tab` state — so it never fights a user-initiated
  // TabsTrigger click (which updates `tab` without touching the URL).
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t !== lastTabParamRef.current) {
      lastTabParamRef.current = t;
      if (t === "urgent") {
        setTab("urgent");
        setLoaded((l) => (l.urgent ? l : { ...l, urgent: true }));
      }
    }
  }, [searchParams]);

  const buildParams = (pageNum) => {
    const params = { page: pageNum, page_size: 50 };
    if (tab !== "all") params.view = tab;
    if (status !== "all") params.status = status;
    if (priority !== "all") params.priority = priority;
    if (projectId !== "all") params.project_id = projectId;
    if (organizationId !== "all") params.organization_id = organizationId;
    if (bucket !== "all") params.bucket = bucket;
    if (search.trim()) params.search = search.trim();
    if (assignedToMe) params.assigned_to = user.id;
    if (clientReplied) params.client_replied = true;
    const [from, to] = dateWindow(dateRange, customFrom, customTo);
    if (from) params.created_from = from.toISOString();
    if (to) params.created_to = to.toISOString();
    return params;
  };

  // Guards against out-of-order responses (e.g. a slow page-1 request
  // resolving after a newer one was already issued by a later filter change).
  const requestIdRef = useRef(0);
  const fetchPage = (pageNum, append = false) => {
    const reqId = ++requestIdRef.current;
    setLoading(true);
    api.get("/tickets", { params: buildParams(pageNum) })
      .then((r) => {
        if (reqId !== requestIdRef.current) return;
        setTotal(r.data.total);
        setPage(r.data.page);
        setItems((prev) => (append ? [...prev, ...r.data.items] : r.data.items));
      })
      .catch(() => {
        if (reqId !== requestIdRef.current) return;
        if (!append) {
          setItems([]);
          setTotal(0);
        }
        toast.error("Could not load tickets. Try again.");
      })
      .finally(() => {
        if (reqId === requestIdRef.current) setLoading(false);
      });
  };

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data));
  }, []);

  // Single debounced fetch keyed on every filter + the active tab.
  // Active always loads. Completed/All only load once `loaded[tab]` is true —
  // either because the user pressed "Load tickets", or because they changed a
  // filter while the tab's prompt was showing (which counts as opting in).
  const prevTabRef = useRef(tab);
  const debounceRef = useRef(null);
  useEffect(() => {
    const tabChanged = prevTabRef.current !== tab;
    prevTabRef.current = tab;
    // Orphan any in-flight request right away — a slow response (e.g. a
    // load-more append) must never land under the new tab/filter selection,
    // including during the debounce window before the next fetch is issued.
    requestIdRef.current++;
    if (tab !== "active" && !loaded[tab] && tabChanged) {
      // Freshly switched to a gated tab: show the prompt, schedule nothing.
      setLoading(false);
      return;
    }
    setLoading(true); // immediate feedback; avoids showing the previous selection's stale rows
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      // Reaching here on a not-yet-loaded gated tab means a filter changed
      // while its prompt was showing — that counts as opting in.
      if (tab !== "active" && !loaded[tab]) setLoaded((l) => ({ ...l, [tab]: true }));
      fetchPage(1);
    }, 250);
    return () => {
      if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, status, priority, projectId, organizationId, bucket, dateRange, customFrom, customTo, search, assignedToMe, clientReplied]);

  const remove = async (t, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Delete ticket ${t.ticket_no}? This permanently removes it and all its comments, work logs and history.`)) return;
    try {
      await api.delete(`/tickets/${t.id}`);
      setItems((prev) => prev.filter((x) => x.id !== t.id));
      setTotal((n) => Math.max(0, n - 1));
      toast.success(`Ticket ${t.ticket_no} deleted`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not delete ticket");
    }
  };

  const isDone = (t) => DONE_BUCKETS.has(byKey[t.status]?.bucket);

  const DAY = 864e5;
  // thresholds are in working days — on a 24x7 clock a working day is 24h,
  // so this matches the old wall-clock behavior exactly
  const ageColor = (workDays) => (workDays > 7 ? "text-rose-600" : workDays > 2 ? "text-amber-600" : "text-slate-600");
  const ageInfo = (t) => {
    if (isDone(t)) {
      if (t.resolved_at) {
        const ms = t.resolution_seconds != null ? t.resolution_seconds * 1000 : new Date(t.resolved_at) - new Date(t.created_at);
        return { primary: `in ${humanizeDuration(ms) || "<1m"}`, cls: "text-slate-600", date: formatDate(t.resolved_at) };
      }
      return { primary: "—", cls: "text-slate-400", date: formatDate(t.created_at) };
    }
    const ms = t.open_age_seconds != null ? t.open_age_seconds * 1000 : Date.now() - new Date(t.created_at);
    const workDays = t.age_work_days != null ? t.age_work_days : ms / DAY;
    return { primary: `open ${humanizeDuration(ms) || "<1m"}`, cls: ageColor(workDays), date: formatDate(t.created_at) };
  };
  const AgeCell = ({ t }) => {
    const a = ageInfo(t);
    return (
      <>
        <div className={a.cls}>{a.primary}</div>
        <div className="text-[11px] text-slate-400">{a.date}</div>
      </>
    );
  };
  const AgeInline = ({ t }) => {
    const a = ageInfo(t);
    return <span className={`ml-auto whitespace-nowrap ${a.cls}`} title={a.date}>{a.primary}</span>;
  };

  // Sorts the currently-loaded rows in place (client-side); resets to
  // server order (newest first) whenever filters/tab change or more load.
  const ageMs = (t) => (isDone(t)
    ? (t.resolution_seconds != null ? t.resolution_seconds * 1000 : new Date(t.resolved_at || t.created_at) - new Date(t.created_at))
    : (t.open_age_seconds != null ? t.open_age_seconds * 1000 : Date.now() - new Date(t.created_at)));
  const SORTERS = {
    project: (t) => (t.project?.name || "").toLowerCase(),
    status: (t) => byKey[t.status]?.order ?? 0,
    priority: (t) => { const i = PRIORITIES.indexOf(t.priority); return i === -1 ? PRIORITIES.length : i; },
    assignee: (t) => (t.assignee?.name || "￿").toLowerCase(),
    age: (t) => ageMs(t),
  };
  const toggleSort = (key) => {
    if (sortBy !== key) { setSortBy(key); setSortDir("asc"); }
    else if (sortDir === "asc") setSortDir("desc");
    else { setSortBy(null); setSortDir("asc"); }
  };
  const sortedItems = useMemo(() => {
    if (!sortBy) return items;
    const get = SORTERS[sortBy];
    const sign = sortDir === "asc" ? 1 : -1;
    return [...items].sort((a, b) => {
      const va = get(a), vb = get(b);
      if (va < vb) return -1 * sign;
      if (va > vb) return 1 * sign;
      return 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, sortBy, sortDir, byKey]);
  const SortHeader = ({ sortKey, children, className = "" }) => (
    <th className={className}>
      <button
        type="button"
        onClick={() => toggleSort(sortKey)}
        className="inline-flex items-center gap-1 hover:text-slate-700"
        data-testid={`tickets-sort-${sortKey}`}
      >
        {children}
        {sortBy === sortKey
          ? (sortDir === "asc" ? <CaretUp size={11} weight="bold" /> : <CaretDown size={11} weight="bold" />)
          : <CaretUpDown size={11} className="opacity-40" />}
      </button>
    </th>
  );

  // tab-scoped status dropdown options
  const isClientForStatusOptions = ["client_admin", "client_user"].includes(user.role);
  const statusOptions = useMemo(() => {
    const all = statusList.length > 0
      ? statusList.filter((s) => s.enabled && (isClientForStatusOptions || !s.hide_from_staff))
      : ALL_STATUSES.map((s) => ({ key: s, label: STATUS_LABELS[s], bucket: undefined }));
    if (tab === "all") return all;
    const wanted = tab === "completed" ? DONE_BUCKETS : ACTIVE_BUCKETS;
    return all.filter((s) => wanted.has(s.bucket));
  }, [statusList, tab, isClientForStatusOptions]);

  const changeTab = (v) => { setTab(v); setStatus("all"); };
  const resetFilters = () => {
    setStatus("all"); setPriority("all"); setProjectId("all");
    setDateRange("all"); setCustomFrom(""); setCustomTo(""); setSearch(""); setAssignedToMe(false);
    setClientReplied(false);
  };

  const showTable = tab === "active" || loaded[tab];

  return (
    <div className="space-y-5" data-testid="tickets-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-blue-600 font-semibold">All requests</div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 mt-1">Tickets</h1>
          <p className="text-sm text-slate-500 mt-1">Track, prioritize and execute every client request from one place.</p>
        </div>
        {canCreateTickets(user) && (
          <Link to="/tickets/new">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="tickets-new-button">
              <Plus size={14} weight="bold" className="mr-1.5" /> Create ticket
            </Button>
          </Link>
        )}
      </div>

      {/* Active / Completed / All tabs */}
      <Tabs value={tab} onValueChange={changeTab}>
        <TabsList data-testid="tickets-tabs">
          <TabsTrigger value="active" data-testid="tickets-tab-active">Active{tab === "active" && loaded.active && !loading ? ` (${total})` : ""}</TabsTrigger>
          {isStaff && (
            <TabsTrigger value="urgent" data-testid="tickets-tab-urgent">Urgent{tab === "urgent" && loaded.urgent && !loading ? ` (${total})` : ""}</TabsTrigger>
          )}
          <TabsTrigger value="completed" data-testid="tickets-tab-completed">Completed{tab === "completed" && loaded.completed && !loading ? ` (${total})` : ""}</TabsTrigger>
          <TabsTrigger value="all" data-testid="tickets-tab-all">All{tab === "all" && loaded.all && !loading ? ` (${total})` : ""}</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Filters */}
      <div className="surface-card p-3 flex flex-wrap items-center gap-2" data-testid="tickets-filters">
        <div className="flex-1 min-w-[220px] flex items-center gap-2 border border-border rounded-md px-3 py-1.5 bg-white">
          <MagnifyingGlass size={16} className="text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by subject, ticket #, description…"
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-slate-400"
            data-testid="tickets-search-input"
          />
        </div>
        <SearchableSelect
          value={status}
          onValueChange={setStatus}
          options={[
            { value: "all", label: "All statuses" },
            ...statusOptions.map((s) => ({ value: s.key, label: s.label })),
          ]}
          placeholder="Status"
          className="w-[180px]"
          testId="filter-status"
        />
        {fieldOn("priority") && (
          <SearchableSelect
            value={priority}
            onValueChange={setPriority}
            options={[
              { value: "all", label: "Any priority" },
              ...PRIORITIES.map((p) => ({ value: p, label: p.charAt(0).toUpperCase() + p.slice(1) })),
            ]}
            placeholder="Priority"
            className="w-[150px]"
            testId="filter-priority"
          />
        )}
        <SearchableSelect
          value={projectId}
          onValueChange={setProjectId}
          options={[
            { value: "all", label: "All projects" },
            ...projects.map((p) => ({ value: p.id, label: p.name })),
          ]}
          placeholder="Project"
          className="w-[180px]"
          testId="filter-project"
        />
        <SearchableSelect
          value={dateRange}
          onValueChange={setDateRange}
          options={[
            { value: "all", label: "All time" },
            { value: "7d", label: "Last 7 days" },
            { value: "30d", label: "Last 30 days" },
            { value: "month", label: "This month" },
            { value: "custom", label: "Custom range…" },
          ]}
          placeholder="Date"
          className="w-[150px]"
          testId="filter-date"
        />
        {dateRange === "custom" && (
          <>
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
              className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-700" data-testid="filter-date-from" />
            <span className="text-xs text-slate-400">to</span>
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
              className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-700" data-testid="filter-date-to" />
          </>
        )}
        {isStaff && (
          <>
            <button
              type="button"
              onClick={() => setAssignedToMe((v) => !v)}
              aria-pressed={assignedToMe}
              data-testid="filter-assigned-to-me"
              className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
                assignedToMe
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"
              }`}
            >
              Assigned to me
            </button>
            <button
              type="button"
              onClick={() => setClientReplied((v) => !v)}
              aria-pressed={clientReplied}
              data-testid="filter-client-replied"
              className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
                clientReplied
                  ? "bg-amber-500 text-white border-amber-500"
                  : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"
              }`}
            >
              Client replied
            </button>
          </>
        )}
        <button
          onClick={resetFilters}
          className="text-xs text-slate-500 hover:text-slate-900 px-2 py-1.5 inline-flex items-center gap-1"
          data-testid="filter-clear"
        >
          <FunnelSimple size={14} /> Reset
        </button>
      </div>

      {!showTable ? (
        <div className="surface-card p-8 text-center space-y-3" data-testid="tickets-filter-prompt">
          <div className="text-sm font-medium text-slate-700">
            {tab === "completed" ? "Completed tickets can pile up over time." : "This is your full ticket history."}
          </div>
          <div className="text-xs text-slate-500">
            Narrow it down with the filters above (status, project, date, search…) — or load the latest 50 straight away.
          </div>
          <Button onClick={() => {
            // Cancel any pending debounce for this tab (e.g. a filter tweak a
            // moment ago) so the button and the timer can't both fetch.
            if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
            setLoaded((l) => ({ ...l, [tab]: true }));
            fetchPage(1);
          }}
            className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="tickets-load-button">
            Load tickets
          </Button>
        </div>
      ) : (
        <>
          {/* Table — md+ only */}
          <div className="hidden md:block surface-card overflow-x-auto">
            <table className="w-full text-sm table-tight" data-testid="tickets-table">
              <thead className="bg-slate-50">
                <tr className="text-left text-[10px] uppercase tracking-[0.1em] text-slate-500">
                  <th>Ticket</th>
                  <th>Subject</th>
                  <SortHeader sortKey="project" className="hidden md:table-cell">Project</SortHeader>
                  <SortHeader sortKey="status">Status</SortHeader>
                  {fieldOn("priority") && <SortHeader sortKey="priority">Priority</SortHeader>}
                  <SortHeader sortKey="assignee" className="hidden lg:table-cell">Assignee</SortHeader>
                  <SortHeader sortKey="age" className="hidden lg:table-cell">Age</SortHeader>
                  {isAdmin && <th></th>}
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={(isAdmin ? 8 : 7) - (fieldOn("priority") ? 0 : 1)} className="text-center text-slate-400 py-12 text-xs">Loading tickets…</td></tr>}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={(isAdmin ? 8 : 7) - (fieldOn("priority") ? 0 : 1)} className="text-center text-slate-400 py-16">
                    <div className="font-medium text-slate-600">No tickets found</div>
                    <div className="text-xs mt-1">Try adjusting your filters, or create a new ticket.</div>
                  </td></tr>
                )}
                {!loading && sortedItems.map((t) => (
                  <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50 transition-colors group">
                    <td>
                      <Link to={`/tickets/${t.id}`} className="font-mono text-blue-600 hover:underline" data-testid={`ticket-row-${t.ticket_no}`}>{t.ticket_no}</Link>
                    </td>
                    <td className="max-w-md">
                      <Link to={`/tickets/${t.id}`} className="block font-medium text-slate-800 truncate group-hover:text-blue-600">{t.subject}</Link>
                      {isStaff && <ClientRepliedBadge at={t.client_replied_at} className="mt-0.5" />}
                      {fieldOn("request_type") && <div className="text-xs text-slate-400 truncate">{t.request_type?.replace(/_/g, " ")}{subtypesText(t) ? ` · ${subtypesText(t)}` : ""}</div>}
                    </td>
                    <td className="hidden md:table-cell text-slate-500">
                      {t.project?.id ? (
                        <Link to={`/projects/${t.project.id}`} className="hover:text-blue-600 hover:underline">{t.project.name}</Link>
                      ) : (t.project?.name || "—")}
                    </td>
                    <td><StatusBadge status={t.status} /></td>
                    {fieldOn("priority") && <td><PriorityBadge priority={t.priority} /></td>}
                    <td className="hidden lg:table-cell">
                      {t.assignee ? (
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[10px] font-semibold">{initials(t.assignee.name)}</div>
                          {t.assignee.id ? (
                            <Link to={`/users/${t.assignee.id}`} className="text-xs text-slate-700 hover:text-blue-600 hover:underline">{t.assignee.name}</Link>
                          ) : (
                            <span className="text-xs text-slate-700">{t.assignee.name}</span>
                          )}
                        </div>
                      ) : <span className="text-xs text-slate-400">Unassigned</span>}
                    </td>
                    <td className="hidden lg:table-cell text-xs whitespace-nowrap">
                      <AgeCell t={t} />
                      {isStaff && t.sla_due_at && (() => {
                        const sla = slaState(t.sla_due_at, t.sla_started_at);
                        if (!sla) return null;
                        const cls = sla.tone === "rose" ? "text-rose-600" : sla.tone === "amber" ? "text-amber-600" : "text-slate-500";
                        return <div className={`${cls} mt-0.5`} data-testid={`ticket-sla-${t.ticket_no}`}>{sla.label}</div>;
                      })()}
                    </td>
                    {isAdmin && (
                      <td className="text-right">
                        <button
                          onClick={(e) => remove(t, e)}
                          className="text-slate-300 hover:text-rose-600 transition-colors"
                          title={`Delete ${t.ticket_no}`}
                          data-testid={`ticket-delete-${t.ticket_no}`}
                        >
                          <Trash size={15} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-4 py-2.5 border-t border-slate-100 text-xs text-slate-500 bg-slate-50">
              <span data-testid="tickets-count">{items.length} ticket{items.length === 1 ? "" : "s"}</span>
            </div>
          </div>

          {/* Mobile card list — hidden at md+ */}
          <div className="md:hidden space-y-2" data-testid="tickets-cards">
            {loading && (
              <div className="text-center text-slate-400 py-12 surface-card text-xs">Loading tickets…</div>
            )}
            {!loading && items.length === 0 && (
              <div className="text-center text-slate-400 py-10 surface-card">
                <div className="font-medium text-slate-600">No tickets found</div>
                <div className="text-xs mt-1">Try adjusting your filters, or create a new ticket.</div>
              </div>
            )}
            {!loading && items.map((t) => (
              <div
                key={t.id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/tickets/${t.id}`)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(`/tickets/${t.id}`); } }}
                className="w-full text-left surface-card p-3 active:bg-slate-50 cursor-pointer"
                data-testid={`ticket-card-${t.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-[11px] text-slate-500">{t.ticket_no}</span>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={t.status} />
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={(e) => remove(t, e)}
                        className="text-slate-300 hover:text-rose-600 transition-colors"
                        title={`Delete ${t.ticket_no}`}
                        data-testid={`ticket-delete-card-${t.ticket_no}`}
                      >
                        <Trash size={15} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="font-medium text-slate-800 mt-1 line-clamp-2">{t.subject}</div>
                {isStaff && <ClientRepliedBadge at={t.client_replied_at} className="mt-1" />}
                {fieldOn("request_type") && t.request_type && (
                  <div className="text-xs text-slate-400 mt-0.5">{t.request_type.replace(/_/g, " ")}{subtypesText(t) ? ` · ${subtypesText(t)}` : ""}</div>
                )}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-slate-500">
                  {t.project?.id ? (
                    <Link to={`/projects/${t.project.id}`} onClick={(e) => e.stopPropagation()} className="hover:text-blue-600 hover:underline">{t.project.name}</Link>
                  ) : <span>{t.project?.name || "—"}</span>}
                  {fieldOn("priority") && <PriorityBadge priority={t.priority} />}
                  {t.assignee ? (
                    <div className="flex items-center gap-1">
                      <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[9px] font-semibold">{initials(t.assignee.name)}</div>
                      {t.assignee.id ? (
                        <Link to={`/users/${t.assignee.id}`} onClick={(e) => e.stopPropagation()} className="hover:text-blue-600 hover:underline">{t.assignee.name}</Link>
                      ) : (
                        <span>{t.assignee.name}</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-slate-400">Unassigned</span>
                  )}
                  <AgeInline t={t} />
                  {isStaff && t.sla_due_at && (() => {
                    const sla = slaState(t.sla_due_at, t.sla_started_at);
                    if (!sla) return null;
                    const cls = sla.tone === "rose" ? "text-rose-600" : sla.tone === "amber" ? "text-amber-600" : "text-slate-500";
                    return <span className={`whitespace-nowrap ${cls}`} data-testid={`ticket-sla-${t.ticket_no}`}>{sla.label}</span>;
                  })()}
                </div>
              </div>
            ))}
            {!loading && items.length > 0 && (
              <div className="px-1 py-1 text-xs text-slate-400" data-testid="tickets-count-mobile">
                {items.length} ticket{items.length === 1 ? "" : "s"}
              </div>
            )}
          </div>

          {/* Pagination — shared by table and cards */}
          {!loading && items.length > 0 && (
            <div className="flex flex-col items-center gap-2 py-1">
              <div className="text-xs text-slate-400">Showing {items.length} of {total}</div>
              {items.length < total && (
                <Button
                  onClick={() => fetchPage(page + 1, true)}
                  variant="outline"
                  className="border-slate-300"
                  data-testid="tickets-load-more"
                >
                  Load more
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
